import React from 'react';
import { i18n } from '@lingui/core';
import { messages as enMessages } from '../locales/en/messages.ts';
import { messages as deMessages } from '../locales/de/messages.ts';
import { messages as frMessages } from '../locales/fr/messages.ts';
import { messages as plMessages } from '../locales/pl/messages.ts';
import { messages as csMessages } from '../locales/cs/messages.ts';
import { messages as rmMessages } from '../locales/rm/messages.ts';
import { messages as chiMessages } from '../locales/chi/messages.ts';
import { messages as itMessages } from '../locales/it/messages.ts';
import { I18nProvider } from '@lingui/react';
import { Language } from '../valueobject/language.ts';
import LanguageBaseProvider from './language-base-provider.tsx';
import { LANGUAGE_STORAGE_KEY } from '../view-model/use-language-view-model.tsx';

export interface LanguageProviderProps {
  children?: React.ReactNode;
}

// all languages must be loaded first and one has to be activated
i18n.load({
  en: enMessages,
  de: deMessages,
  fr: frMessages,
  pl: plMessages,
  cs: csMessages,
  rm: rmMessages,
  chi: chiMessages,
  it: itMessages,
});

function findSupportedLanguage(requestedLanguage: string | null | undefined): Language | null {
  if (requestedLanguage == null) {
    return null;
  }

  const supportedLanguageList = Object.values(Language);
  const requestedLanguageLowerCase = requestedLanguage.toLowerCase();

  for (const supportedLanguage of supportedLanguageList) {
    if (supportedLanguage.toLowerCase() === requestedLanguageLowerCase) {
      return supportedLanguage;
    }
  }

  if (requestedLanguageLowerCase.includes('-')) {
    const baseLanguagePart = requestedLanguageLowerCase.split('-')[0];

    for (const supportedLanguage of supportedLanguageList) {
      if (supportedLanguage.toLowerCase() === baseLanguagePart) {
        return supportedLanguage;
      }
    }
  }

  return null;
}

const browserLanguage = window.navigator.language;
const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
const initialLanguage = findSupportedLanguage(storedLanguage) || findSupportedLanguage(browserLanguage) || Language.EN;

i18n.activate(initialLanguage);

/**
 * A provider component that initializes and manages language and internationalization (i18n) to use the lingui functions
 * in child components. (t`Text` and <Trans></Trans>)
 */
export default function LanguageProvider({ children }: LanguageProviderProps) {
  return (
    <I18nProvider i18n={i18n}>
      <LanguageBaseProvider>{children} </LanguageBaseProvider>{' '}
    </I18nProvider>
  );
}
